import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from '@/components/ui/dialog';
import { ImageIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${BACKEND}${url}`;
};

export default function PhotoGallery() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        const { data } = await axios.get(`${API}/photos`);
        setPhotos(data);
      } catch (error) {
        console.error('Failed to fetch photos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPhotos();
  }, []);

  return (
    <Card className="bg-[#131C2F] border border-white/10 p-6" data-testid="photo-gallery">
      <h3 className="text-2xl font-bold text-white mb-6">Photo Gallery</h3>
      {loading ? (
        <p className="text-[#9BA3AF]">Loading photos...</p>
      ) : photos.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <ImageIcon className="w-16 h-16 text-[#9BA3AF] mb-4" />
          <p className="text-[#9BA3AF]">No photos uploaded yet.</p>
          <p className="text-[#9BA3AF] text-sm mt-2">Admins can upload photos from the dashboard.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {photos.map((photo) => (
            <Dialog key={photo.id}>
              <DialogTrigger asChild>
                <button
                  type="button"
                  data-testid={`photo-${photo.id}`}
                  className="aspect-square overflow-hidden rounded-lg cursor-pointer group relative block w-full p-0 border-0 bg-transparent"
                >
                  <img
                    src={resolveUrl(photo.url)}
                    alt={photo.caption || 'Match photo'}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  {photo.caption && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3 pointer-events-none">
                      <p className="text-white text-sm">{photo.caption}</p>
                    </div>
                  )}
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl bg-[#0A0F1C] border-white/10">
                <img src={resolveUrl(photo.url)} alt={photo.caption || 'Match photo'} className="w-full rounded-lg" />
                {photo.caption && <p className="text-white text-center mt-4">{photo.caption}</p>}
              </DialogContent>
            </Dialog>
          ))}
        </div>
      )}
    </Card>
  );
}
